/**
 * Deterministic seeded RNG for CraftForce match state.
 *
 * Uses a hash-based counter approach: each call derives a value from
 * (seed + ":" + callIndex). This lets us reproduce any random value
 * by knowing the seed and the call index, which is stored in MatchState.
 */

function cyrb53(str: string, seed = 0): number {
  let h1 = 0xdeadbeef ^ seed
  let h2 = 0x41c6ce57 ^ seed
  for (let i = 0; i < str.length; i++) {
    const ch = str.charCodeAt(i)
    h1 = Math.imul(h1 ^ ch, 2654435761)
    h2 = Math.imul(h2 ^ ch, 1597334677)
  }
  h1 =
    Math.imul(h1 ^ (h1 >>> 16), 2246822507) ^
    Math.imul(h2 ^ (h2 >>> 13), 3266489909)
  h2 =
    Math.imul(h2 ^ (h2 >>> 16), 2246822507) ^
    Math.imul(h1 ^ (h1 >>> 13), 3266489909)
  return (4294967296 * (2097151 & h2) + (h1 >>> 0)) / 9007199254740992
}

/** Returns a float in [0, 1) for the given seed and call index. */
export function rngValue(seed: string, callIndex: number): number {
  return cyrb53(`${seed}:${callIndex}`)
}

/** Returns an integer in [0, max) for the given seed and call index. */
export function rngInt(seed: string, callIndex: number, max: number): number {
  return Math.floor(rngValue(seed, callIndex) * max)
}

/** Fisher-Yates shuffle using the seeded RNG. Mutates the array. */
export function seededShuffle<T>(
  arr: T[],
  seed: string,
  startCallIndex: number,
): number {
  let callIndex = startCallIndex
  for (let i = arr.length - 1; i > 0; i--) {
    const j = rngInt(seed, callIndex++, i + 1)
    ;[arr[i], arr[j]] = [arr[j], arr[i]]
  }
  return callIndex // return the next call index after the shuffle
}
